import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

export interface AmadeusStatus {
  state: 'valid' | 'refreshing' | 'error' | 'unknown';
  session_exists: boolean;
  session_age_seconds: number | null;
  last_check: string | null;
  last_check_seconds_ago: number | null;
  last_refresh: string | null;
  consecutive_failures: number;
  circuit_breaker_open: boolean;
  circuit_breaker_until: string | null;
  error_message: string | null;
}

interface UseAmadeusStatusOptions {
  /** Polling interval in milliseconds (default: 30000 = 30 seconds) */
  pollInterval?: number;
  /** Whether to poll when tab is hidden (default: false) */
  pollWhenHidden?: boolean;
  /** Whether to start polling immediately (default: true) */
  enabled?: boolean;
}

/**
 * Hook to fetch and poll Amadeus session status.
 *
 * Polls the /api/chat/amadeus-status/ endpoint to get current session state.
 * Automatically pauses polling when tab is hidden (unless pollWhenHidden is true).
 *
 * @example
 * ```tsx
 * const { status, loading, error, refetch } = useAmadeusStatus();
 *
 * if (loading) return <Spinner />;
 * if (status?.state === 'valid') return <GreenDot />;
 * ```
 */
export function useAmadeusStatus(options: UseAmadeusStatusOptions = {}) {
  const {
    pollInterval = 30000,
    pollWhenHidden = false,
    enabled = true,
  } = options;

  const [status, setStatus] = useState<AmadeusStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!enabled) return;

    try {
      const response = await api.get('/api/chat/amadeus-status/');
      setStatus(response.data);
      setError(null);
    } catch (err: any) {
      console.error('[useAmadeusStatus] Error fetching status:', err);
      setError(err.message || 'Failed to fetch Amadeus status');
      // Set a default error status
      setStatus({
        state: 'error',
        session_exists: false,
        session_age_seconds: null,
        last_check: null,
        last_check_seconds_ago: null,
        last_refresh: null,
        consecutive_failures: 0,
        circuit_breaker_open: false,
        circuit_breaker_until: null,
        error_message: err.message || 'Failed to fetch status',
      });
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchStatus();

    // Set up polling interval
    const intervalId = setInterval(() => {
      // Skip polling when tab is hidden (unless configured otherwise)
      if (!pollWhenHidden && document.visibilityState === 'hidden') {
        return;
      }
      fetchStatus();
    }, pollInterval);

    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, [fetchStatus, pollInterval, pollWhenHidden, enabled]);

  // Refetch when tab becomes visible (if we were hidden)
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchStatus, enabled]);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus,
  };
}

export default useAmadeusStatus;
